/**
 * Document Details Content
 * Detalhe/perfil de um documento/artigo da base (KB).
 * Redesign Design System: toolbar 3-zonas com breadcrumb (nunca seta-voltar),
 * corpo do artigo à esquerda + sidebar de metadados à direita (empilha no mobile).
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import type {
  DatabaseDocumentResponse,
  DatabaseDocumentData,
  DocumentCategory,
  DocumentItemStatus,
} from '@troia-v3/types';
import { Breadcrumb } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { formatDuration } from '@/shared/utils/formatDuration';
import { useDatabase } from '../../hooks/useDatabases';

interface DocumentDetailsContentProps {
  document: DatabaseDocumentResponse<DatabaseDocumentData>;
  databaseId: string;
  onEdit?: () => void;
  onDelete?: () => void;
}

const categoryLabels: Record<DocumentCategory, string> = {
  article: 'Artigo',
  manual: 'Manual',
  faq: 'FAQ',
  policy: 'Política',
  guide: 'Guia',
  tutorial: 'Tutorial',
  report: 'Relatório',
  whitepaper: 'Whitepaper',
  case_study: 'Caso de Estudo',
  other: 'Outros',
};

const statusLabels: Record<DocumentItemStatus, string> = {
  published: 'Publicado',
  draft: 'Rascunho',
  archived: 'Arquivado',
};

const getStatusColor = (status: DocumentItemStatus): { bg: string; text: string } => {
  const colors: Record<string, { bg: string; text: string }> = {
    published: { bg: 'var(--theme-success-background)', text: 'var(--theme-success-color)' },
    draft: { bg: 'var(--theme-warning-background)', text: 'var(--theme-warning-color)' },
    archived: { bg: 'var(--theme-muted-text)', text: '#ffffff' },
  };
  return colors[status] || { bg: 'var(--theme-card-background)', text: 'var(--theme-muted-text)' };
};

const languageLabels: Record<string, string> = {
  'pt-BR': 'Português (BR)',
  'en-US': 'English (US)',
  es: 'Español',
};

const sourceLabels: Record<string, string> = {
  manual: 'Manual',
  integration: 'Integração',
  import: 'Importação',
  api: 'API',
};

interface TocItem {
  id: string;
  text: string;
  level: number;
}

export const DocumentDetailsContent: React.FC<DocumentDetailsContentProps> = ({
  document,
  databaseId,
  onEdit,
  onDelete,
}) => {
  const navigate = useNavigate();
  const doc = document.data;
  const statusColor = getStatusColor(doc.itemStatus);

  // Nome da base para o breadcrumb (rota de módulo — usuário já tem acesso).
  const { data: database } = useDatabase(databaseId);

  const handleBackToDatabases = () => navigate('/databases');
  const handleBackToDatabase = () => navigate(`/databases/${databaseId}`);

  // Sanitize HTML content for safe rendering
  const rawContent = doc.content || '';
  // Detecta se o conteúdo é texto puro (sem tags HTML) — neste caso o
  // navegador colapsaria quebras de linha em espaços. Aplicamos
  // `whitespace-pre-wrap` pra preservar a formatação.
  const isPlainText = !/<[a-zA-Z][^>]*>/.test(rawContent);

  // Sanitiza + extrai o índice ("Neste documento") dos headings reais do conteúdo.
  const { contentHtml, toc } = useMemo<{ contentHtml: string; toc: TocItem[] }>(() => {
    const sanitized = DOMPurify.sanitize(rawContent);
    if (isPlainText) return { contentHtml: sanitized, toc: [] };
    try {
      const parsed = new DOMParser().parseFromString(sanitized, 'text/html');
      const headings = Array.from(parsed.querySelectorAll('h1, h2, h3'));
      const items: TocItem[] = [];
      const used = new Set<string>();
      headings.forEach((h, i) => {
        const text = (h.textContent || '').trim();
        if (!text) return;
        const base =
          text
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '') || `sec-${i}`;
        let id = base;
        let n = 1;
        while (used.has(id)) id = `${base}-${n++}`;
        used.add(id);
        h.setAttribute('id', id);
        items.push({ id, text, level: Number(h.tagName.substring(1)) });
      });
      return { contentHtml: parsed.body.innerHTML, toc: items };
    } catch {
      return { contentHtml: sanitized, toc: [] };
    }
  }, [rawContent, isPlainText]);

  const scrollToHeading = (id: string) => {
    window.document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const categoryLabel = categoryLabels[doc.category] || doc.category;
  const source = document.metadata?.source;

  // Linhas da tabela de metadados (algumas condicionais).
  const metaRows: { label: string; value: React.ReactNode }[] = [
    {
      label: 'Categoria',
      value: (
        <span
          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
          style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
        >
          {categoryLabel}
        </span>
      ),
    },
    {
      label: 'Status',
      value: (
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold"
          style={{ backgroundColor: statusColor.bg, color: statusColor.text }}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'currentColor' }} />
          {statusLabels[doc.itemStatus] || doc.itemStatus}
        </span>
      ),
    },
  ];
  if (source) metaRows.push({ label: 'Fonte', value: sourceLabels[source] || source });
  if (doc.language)
    metaRows.push({ label: 'Idioma', value: languageLabels[doc.language] || doc.language });
  metaRows.push({
    label: 'Criado',
    value: new Date(document.createdAt).toLocaleDateString('pt-BR'),
  });
  metaRows.push({
    label: 'Atualizado',
    value: new Date(document.updatedAt).toLocaleDateString('pt-BR'),
  });

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'var(--theme-card-background)',
    border: '1px solid var(--theme-card-border)',
  };

  return (
    <div
      className="h-full flex flex-col"
      style={{ backgroundColor: 'var(--theme-page-background)' }}
      data-testid="document-details-content"
    >
      {/* Toolbar 3 zonas (DS 3.3): breadcrumb à esquerda, ações à direita */}
      <header
        className="flex-none h-14 flex items-center gap-3 px-4 sm:px-6 border-b"
        style={{
          backgroundColor: 'var(--theme-header-background)',
          borderColor: 'var(--theme-header-border)',
        }}
      >
        {/* Breadcrumb shared (F25-31) — mobile: legenda condensada, nunca seta-voltar */}
        <Breadcrumb
          items={[
            { label: 'Bases de dados', onNavigate: handleBackToDatabases, testid: 'document-breadcrumb-databases' },
            { label: database?.name || 'Base', onNavigate: handleBackToDatabase, testid: 'document-breadcrumb-database' },
            { label: doc.title, testid: 'document-breadcrumb-current' },
          ]}
          mobileLegend={`Bases de dados · ${database?.name || 'Base'}`}
          data-testid="document-breadcrumb"
        />

        <div className="flex items-center gap-2 flex-none">
          {onEdit && (
            <button
              onClick={onEdit}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-semibold transition-colors"
              style={{
                backgroundColor: 'var(--theme-button-primary)',
                color: 'var(--theme-button-primary-text)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--theme-button-primary-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--theme-button-primary)';
              }}
              data-testid="document-edit-button"
            >
              <Icon name="edit" className="w-4 h-4" />
              <span className="hidden sm:inline">Editar</span>
            </button>
          )}
          {onDelete && (
            <button
              onClick={onDelete}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg transition-colors"
              style={{ color: 'var(--theme-error-color)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--theme-error-background)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              title="Excluir"
              data-testid="document-delete-button"
            >
              <Icon name="trash" className="w-[18px] h-[18px]" />
            </button>
          )}
        </div>
      </header>

      {/* Conteúdo */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-8">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_322px] gap-4 md:gap-6 items-start">
            {/* Coluna principal — artigo */}
            <article className="rounded-xl overflow-hidden" style={cardStyle}>
              <div className="p-6 sm:p-8">
                {doc.coverImage && (
                  <img src={doc.coverImage} alt="" className="w-full aspect-video object-cover rounded-lg mb-5" data-testid="document-cover" />
                )}
                <h1
                  className="text-2xl md:text-3xl font-bold tracking-tight mb-3"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  {doc.title}
                </h1>

                {/* art-meta: categoria + autor + atualizado */}
                <div
                  className="flex items-center gap-2 flex-wrap text-sm mb-5"
                  style={{ color: 'var(--theme-muted-text)' }}
                >
                  <span
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                    style={{
                      backgroundColor: 'var(--theme-primary-background)',
                      color: 'var(--theme-primary)',
                    }}
                  >
                    {categoryLabel}
                  </span>
                  {doc.mediaType && (
                    <span
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                      style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
                      data-testid="document-media-badge"
                    >
                      {{ video: 'Vídeo', audio: 'Áudio', frame: 'Incorporado' }[doc.mediaType]}
                      {doc.duration ? ` · ${formatDuration(doc.duration)}` : ''}
                    </span>
                  )}
                  {doc.series && (
                    <span data-testid="document-series">Trilha {doc.series}{doc.seriesOrder !== undefined ? `, aula ${doc.seriesOrder}` : ''}</span>
                  )}
                  {doc.author && (
                    <>
                      <span>por {doc.author}</span>
                      <span aria-hidden>·</span>
                    </>
                  )}
                  <span>
                    Atualizado em {new Date(document.updatedAt).toLocaleDateString('pt-BR')}
                  </span>
                </div>

                {/* Resumo/excerpt */}
                {doc.summary && (
                  <p
                    className="text-sm italic mb-5 pb-5 border-b"
                    style={{
                      color: 'var(--theme-muted-text)',
                      borderColor: 'var(--theme-card-border)',
                    }}
                  >
                    {doc.summary}
                  </p>
                )}

                {/* Corpo do artigo */}
                <div
                  className="prose max-w-none text-sm"
                  style={{
                    color: 'var(--theme-card-text)',
                    ...(isPlainText ? { whiteSpace: 'pre-wrap' } : {}),
                  }}
                  dangerouslySetInnerHTML={{ __html: contentHtml }}
                />
              </div>
            </article>

            {/* Sidebar — metadados */}
            <aside className="flex flex-col gap-4 md:gap-6 min-w-0">
              {/* Metadados */}
              <section className="rounded-xl overflow-hidden" style={cardStyle}>
                <div
                  className="px-4 py-3 border-b text-sm font-semibold"
                  style={{ borderColor: 'var(--theme-card-border)', color: 'var(--theme-card-text)' }}
                >
                  Metadados
                </div>
                {metaRows.map((row, i) => (
                  <div
                    key={row.label}
                    className={`flex gap-3 px-4 py-2.5 text-sm ${i > 0 ? 'border-t' : ''}`}
                    style={i > 0 ? { borderColor: 'var(--theme-card-border)' } : undefined}
                  >
                    <span className="w-20 flex-none" style={{ color: 'var(--theme-muted-text)' }}>
                      {row.label}
                    </span>
                    <div
                      className="flex-1 min-w-0 font-medium break-words"
                      style={{ color: 'var(--theme-card-text)' }}
                    >
                      {row.value}
                    </div>
                  </div>
                ))}
              </section>

              {/* Neste documento (TOC gerado dos headings) */}
              {toc.length > 0 && (
                <section className="rounded-xl overflow-hidden" style={cardStyle}>
                  <div
                    className="px-4 py-3 border-b text-sm font-semibold"
                    style={{
                      borderColor: 'var(--theme-card-border)',
                      color: 'var(--theme-card-text)',
                    }}
                  >
                    Neste documento
                  </div>
                  <nav className="py-2">
                    {toc.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToHeading(item.id)}
                        className="block w-full text-left px-4 py-1.5 text-sm border-l-2 transition-colors truncate"
                        style={{
                          color: 'var(--theme-muted-text)',
                          borderLeftColor: 'transparent',
                          paddingLeft: item.level >= 3 ? 32 : 16,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = 'var(--theme-primary)';
                          e.currentTarget.style.borderLeftColor = 'var(--theme-primary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = 'var(--theme-muted-text)';
                          e.currentTarget.style.borderLeftColor = 'transparent';
                        }}
                        title={item.text}
                        data-testid={`document-toc-${item.id}`}
                      >
                        {item.text}
                      </button>
                    ))}
                  </nav>
                </section>
              )}

              {/* Palavras-chave */}
              {doc.keywords && doc.keywords.length > 0 && (
                <section className="rounded-xl overflow-hidden" style={cardStyle}>
                  <div
                    className="px-4 py-3 border-b text-sm font-semibold"
                    style={{
                      borderColor: 'var(--theme-card-border)',
                      color: 'var(--theme-card-text)',
                    }}
                  >
                    Palavras-chave
                  </div>
                  <div className="flex flex-wrap gap-2 p-4">
                    {doc.keywords.map((keyword, index) => (
                      <span
                        key={index}
                        className="px-2.5 py-1 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: 'var(--theme-info-background)',
                          color: 'var(--theme-info-color)',
                        }}
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {/* Tags */}
              {doc.tags && doc.tags.length > 0 && (
                <section className="rounded-xl overflow-hidden" style={cardStyle}>
                  <div
                    className="px-4 py-3 border-b text-sm font-semibold"
                    style={{
                      borderColor: 'var(--theme-card-border)',
                      color: 'var(--theme-card-text)',
                    }}
                  >
                    Tags
                  </div>
                  <div className="flex flex-wrap gap-2 p-4">
                    {doc.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2.5 py-1 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: 'var(--theme-primary-background)',
                          color: 'var(--theme-primary)',
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {/* Anexos */}
              {doc.attachments && doc.attachments.length > 0 && (
                <section className="rounded-xl overflow-hidden" style={cardStyle}>
                  <div
                    className="px-4 py-3 border-b text-sm font-semibold"
                    style={{
                      borderColor: 'var(--theme-card-border)',
                      color: 'var(--theme-card-text)',
                    }}
                  >
                    Anexos
                  </div>
                  <div className="flex flex-col gap-2 p-4">
                    {doc.attachments.map((attachment, index) => (
                      <a
                        key={index}
                        href={attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 rounded-lg transition-colors"
                        style={{
                          backgroundColor: 'var(--theme-page-background)',
                          border: '1px solid var(--theme-card-border)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--theme-page-background)';
                        }}
                        data-testid={`document-attachment-${index}`}
                      >
                        <Icon
                          name="paperclip"
                          className="w-4 h-4 flex-shrink-0"
                          style={{ color: 'var(--theme-primary)' }}
                        />
                        <div className="min-w-0">
                          <div
                            className="text-sm font-medium truncate"
                            style={{ color: 'var(--theme-card-text)' }}
                          >
                            {attachment.name}
                          </div>
                          <div className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                            {(attachment.size / 1024).toFixed(1)} KB
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </section>
              )}
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
};
